import React, { useEffect, useState } from "react";
import { Control, FieldErrors } from "react-hook-form";
import {
    createFailure,
    DropdownOption,
    FormDropdown,
    getById,
    mapToDropdownOption,
    validationFailed,
    ValidationResult,
    validationSuccessful
} from "sprint-common-web";

import {
    SystemPlatformValidationRequest,
    systemPlatformValidator
} from "../../../../admin/system-platforms/SystemPlatformValidator";
import { EncodeSpecificationContentType } from "../../../../common/types/EncodeSpecificationContentType";
import { handleOptionChange } from "../../../../common/utils/handleOptionChange";
import { SystemPlatform } from "../../../common/types/OrdersAirline";
import { useSystems } from "../common/hooks/useSystems";
import { useCreateOrder } from "../common/provider/useCreateOrder";

interface OrderSystemsSelectorProps {
    label: string;
    name: string;
    control: Control;
    errors: FieldErrors;
    dependsOn: string[];
}

export const OrderSystemsSelector: React.FC<OrderSystemsSelectorProps> = ({
    label,
    name,
    control,
    errors,
    dependsOn
}: OrderSystemsSelectorProps) => {
    const { data: systems } = useSystems();
    const { airline, setSystem } = useCreateOrder();

    const [options, setOptions] = useState<DropdownOption[]>([]);

    useEffect(() => {
        if (airline) {
            const newOptions = airline.systemPlatforms
                .filter(isSystemPlatformValid)
                .map((item) => mapToDropdownOption(item.system));

            setOptions(newOptions);
        } else {
            clearOptions();
            setSystem(undefined);
        }
    }, [airline]);

    const clearOptions = () => {
        setOptions([]);
    };

    const handleSystemSelected = (value: string) => {
        const selectedSystem = getById(systems ?? [], value);

        setSystem(selectedSystem);
    };

    const handleSystemUnselected = () => {
        setSystem(undefined);
    };

    const validateSystemDeliveryEndpoint = async (systemId: string): Promise<ValidationResult> => {
        const systemPlatform = airline?.systemPlatforms.find((item) => item.system.id === systemId);

        if (!systemPlatform?.deliveryEndpoint) {
            const missingDeliveryEndpointError = createFailure(
                `Delivery endpoint is missing for '${systemPlatform?.system.name}'`,
                "delivery-endpoint"
            );

            return validationFailed([missingDeliveryEndpointError]);
        }

        return validationSuccessful();
    };

    return (
        <FormDropdown
            label={label}
            placeholder="Search and select system"
            name={name}
            options={options}
            disabled={!airline}
            dependsOn={dependsOn}
            control={control}
            errors={errors}
            rules={{ required: "Select system", validate: validateSystemDeliveryEndpoint }}
            onChange={handleOptionChange(handleSystemSelected, handleSystemUnselected)}
        />
    );
};

export const isSystemPlatformValid = (systemPlatform: SystemPlatform): boolean => {
    const validator = systemPlatformValidator();

    const movieSpecification = systemPlatform.specifications.find(
        (item) => item.encodeSpecificationContentType === EncodeSpecificationContentType.Movie
    );
    const tvSpecification = systemPlatform.specifications.find(
        (item) => item.encodeSpecificationContentType === EncodeSpecificationContentType.TvSeries
    );

    const validationItem: SystemPlatformValidationRequest = {
        audioProxyType: systemPlatform.audioProxyType,
        copySubtitleType: systemPlatform.copySubtitleType,
        muxSubtitleType: systemPlatform.muxSubtitleType,
        deliveryEndpoint: systemPlatform.deliveryEndpoint,
        movieSpecificationId: movieSpecification?.defaultEncodeSpecification?.id,
        tvSpecificationId: tvSpecification?.defaultEncodeSpecification?.id,
        muxSpecificationId: systemPlatform.muxSpecification?.id,
        buzzIds: systemPlatform.buzzIds,
        shreqIds: systemPlatform.shreqIds
    };

    const validationResult = validator.validate(validationItem);

    return validationResult.failures.length ? false : true;
};
